"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LawQuery = exports.LawQueryItem = exports.BaseLawCriteria = exports.Query = void 0;
const elawsApi_1 = require("../elawsApi");
const util_1 = require("../util");
const lawinfo_1 = require("./lawinfo");
const FetchElawsLoader_1 = require("./loaders/FetchElawsLoader");
const xmlToEL_1 = require("../node/el/xmlToEL");
// eslint-disable-next-line @typescript-eslint/no-require-imports
const DOMParser = (global["window"] && window.DOMParser) || require("@xmldom/xmldom").DOMParser;
const domParser = new DOMParser();
const symbolFinalyzeQueryItem = Symbol("symbolFinalyzeQueryItem");
const symbolDoNotFinalize = Symbol("symbolDoNotFinalize");
const defaultQueryOptions = () => ({
    showProgress: true,
});
/**
 * The query object that represents a source list and a filtering criteria.
 *
 * フィルタ条件と検索元リストを表すクエリオブジェクト。
 *
 * @example
 * A `Query` works as an async generator.
 *
 * `Query` は async generator として使用できます。
 *
 * ```ts
 * const query = new Query(population, criteria);
 * for await (const item of query) {
 *     console.log(item);
 * }
 * ```
 */
class Query {
    population;
    criteria;
    options;
    /**
     * Instanciate a `Query`.
     * @param population - a source list
     * @param criteria - a filtering criteria
     * @param options - options
     */
    constructor(population, criteria, options) {
        this.population = population;
        if (criteria === null) {
            this.criteria = criteria;
        }
        else if ("match" in criteria) {
            this.criteria = criteria;
        }
        else if (typeof criteria === "function") {
            this.criteria = { match: criteria };
        }
        else {
            throw (0, util_1.assertNever)(criteria);
        }
        this.options = { ...defaultQueryOptions(), ...options };
    }
    new(population, criteria, overrideOptions) {
        return new Query(population, criteria, { ...this.options, ...overrideOptions });
    }
    async *[Symbol.asyncIterator]() {
        if (this.criteria === null) {
            yield* this.population;
        }
        else {
            for await (const item of this.population) {
                const matched = await this.criteria.match(item);
                if (!matched)
                    continue;
                yield item;
            }
        }
    }
    /**
     * Apply a function for each filtered item.
     *
     * フィルタ後の要素ごとに関数を実行します。
     *
     * @param func - a function to be called for each filtered item <br/> 要素ごとに実行される関数
     * @param criteria - an additional criteria applied after filtering / 関数実行後に適用するフィルタ条件
     * @param options - options which override the original ones / 上書きするオプション項目
     * @returns - a new `Query` that yields items returned by `func` <br/> `func` の返り値を列挙する新しい `Query`
     */
    map(func, criteria = null, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return new Query((async function* () {
            for await (const item of self) {
                const value = await func(item);
                if (value === undefined) {
                    throw TypeError(`Query.map: the mapped function (${func}) returned an undefined. Please check the definition of the function. (First occurance: ${item})`);
                }
                yield value;
            }
        })(), criteria, options);
    }
    mapWorkers(workersPool, criteria = null, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return new Query((async function* () {
            for await (const [, item, value] of workersPool.run(self)) {
                if (value === undefined) {
                    throw TypeError(`Query.mapWorkers: the WorkersPool returned an undefined. Please check the definition of the function. (First occurance: ${item})`);
                }
                yield value;
            }
        })(), criteria, options);
    }
    /**
     * Apply a function for each filtered item and iterate the merged object with the returned and original object.
     *
     * フィルタ後の要素ごとに関数を実行し、返り値と元の要素のプロパティをマージしたオブジェクトを列挙します。
     *
     * @param func - a function to be called for each filtered item <br/> 要素ごとに実行される関数
     * @param criteria - an additional criteria applied after merge / マージ後に適用するフィルタ条件
     * @param options - options which override the original ones / 上書きするオプション項目
     * @returns - a new `Query` that yields merged objects <br/> マージされたオブジェクトを列挙する新しい `Query`
     */
    assign(func, criteria = null, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.new((async function* () {
            for await (const item of self) {
                const value = await func(item);
                if (value === undefined) {
                    throw TypeError(`Query.assign: the mapped function (${func}) returned an undefined. Please check the definition of the function. (First occurance: ${item})`);
                }
                yield Object.setPrototypeOf({ ...item, ...value }, Object.getPrototypeOf(item));
            }
        })(), criteria, options);
    }
    /**
     * Apply an additional filter.
     *
     * フィルタを追加します。
     *
     * @param criteria - an additional criteria / 追加するフィルタ条件
     * @param options - options which override the original ones / 上書きするオプション項目
     * @returns - a new `Query` that applies `criteria` to the filtered items of the original `Query` <br/> フィルタ後の項目を検索元とし、`criteria` を検索条件とする新しい `Query`
     */
    filter(criteria, options) {
        return this.new(this[Symbol.asyncIterator](), criteria, options);
    }
    /**
     * Yield while `func` returns `true`.
     *
     * `func` が `true` を返す間、列挙を続けます。
     *
     * @param func - a function to be called for each filtered item. Returning `false` terminates the iteration. <br/>要素ごとに実行される関数。`false`を返すと列挙を停止します。
     * @param yieldLast - whether to return the item that caused `func` returned `false` <br/>`func`が`false`を返す要因となった要素を出力するかどうか
     * @param options - options which override the original ones / 上書きするオプション項目
     * @returns - a new `Query` that yields while `func` returns `true`<br/>`func` が `true` を返す間列挙を続ける新しい `Query`
     */
    while(func, yieldLast = false, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.new((async function* () {
            for await (const item of self) {
                const continuing = await func(item);
                if (continuing) {
                    yield item;
                }
                else {
                    if (yieldLast)
                        yield item;
                    break;
                }
            }
        })(), null, options);
    }
    /**
     * Skip specified count.
     *
     * 指定した件数スキップします。
     *
     * @param count - count to skip<br/>スキップする件数
     * @param options - options which override the original ones / 上書きするオプション項目
     * @returns - a new `Query` that yields after skipping specified count.<br/>指定した件数のスキップ後列挙を続ける新しい `Query`
     */
    skip(count, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.new((async function* () {
            let remain = count;
            for await (const item of self) {
                if (remain > 0) {
                    remain--;
                    continue;
                }
                yield item;
            }
        })(), null, options);
    }
    /**
     * Yield until it reaches the maximum count.
     *
     * 出力の最大件数を設定します。
     *
     * @param max - the maximum count<br/>最大件数
     * @param options - options which override the original ones / 上書きするオプション項目
     * @returns - a new `Query` that yields until it reaches the maximum count.<br/>最大件数に達するまで列挙を続ける新しい `Query`
     */
    limit(max, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.new((async function* () {
            if (max <= 0)
                return;
            let count = 0;
            for await (const item of self) {
                yield item;
                count++;
                if (count >= max)
                    break;
            }
        })(), null, options);
    }
    /**
     * Yield a property of each item.
     *
     * 特定のプロパティの内容を一つ抜き出して列挙します。
     *
     * @param key - the key of a property to be picked<br/>抜き出すプロパティのキー
     * @returns - a new `Query` that yields the picked property<br/>抜き出したプロパティの内容を列挙する新しい `Query`
     */
    property(key) {
        return this.map(item => item[key]);
    }
    /**
     * Pick properties of each item.
     *
     * 特定のプロパティ以外のプロパティを削除したオブジェクトを列挙します。
     *
     * @param keys - the keys of properties to be picked<br/>抜き出すプロパティのキー
     * @returns - a new `Query` that yields the objects with the picked properties<br/>プロパティを抜き出した新しいオブジェクトの内容を列挙する新しい `Query`
     */
    pick(...keys) {
        return this.map(item => {
            const picked = {};
            for (const key of keys) {
                picked[key] = item[key];
            }
            return picked;
        });
    }
    /**
     * Generate an array from the `Query`. Running this function will invoke the whole iteration process of the `Query`.
     *
     * `Query` から配列を生成します。この関数を実行すると `Query` の列挙を最後まで実行します。
     *
     * @param options
     *
     * - `options.preserveCache <boolean>`: whether to suggest the `Query` to preserve the cached data for each item, which normally will be cleared after yield (default: `false`)<br/>`Query`にキャッシュされたデータを削除せずそのまま残すかどうかを指示します。
     *
     * @returns - a `Promise` that resolves a generated array<br/>生成された配列を返す `Promise`
     */
    async toArray(options = { preserveCache: false }) {
        const arr = [];
        await this.forEach(item => {
            if (options.preserveCache && item && (typeof item === "object") && (symbolDoNotFinalize in item)) {
                item[symbolDoNotFinalize] = true;
            }
            arr.push(item);
        });
        return arr;
    }
    /**
     * Invoke `func` for each filtered item. Running this function will invoke the whole iteration process of the `Query`.
     *
     * 列挙された要素ごとに `func` を実行します。この関数を実行すると `Query` の列挙を最後まで実行します。
     *
     * @param func - a function to be called for each item<br/>要素ごとに実行される関数
     */
    async forEach(func) {
        const startTime = new Date();
        let lastMessageTime = startTime;
        let matchCount = 0;
        for await (const item of this) {
            matchCount++;
            await func(item);
            const now = new Date();
            if (now.getTime() - lastMessageTime.getTime() > 1000) {
                if (this.options.showProgress)
                    console.info(`Query progress:\t⌛ running...\t(${matchCount.toString().padStart(4, " ")} matches\tin ${now.getTime() - startTime.getTime()} msec)`);
                lastMessageTime = now;
            }
            if (item && typeof item === "object" && symbolFinalyzeQueryItem in item) {
                item[symbolFinalyzeQueryItem]();
            }
        }
        const now = new Date();
        const msec = now.getTime() - startTime.getTime();
        if (this.options.showProgress)
            console.info(`Query progress:\t✓ completed.\t(${matchCount.toString().padStart(4, " ")} matches\tin ${msec} msec)`);
    }
}
exports.Query = Query;
const getDefaultArgs = (validators) => {
    const ret = {};
    for (const key of Object.keys(validators)) {
        ret[key] = validators[key].default();
    }
    return ret;
};
const RegExpValidator = {
    typeDescription: "RegExp",
    default: () => undefined,
    validate: (v) => v === undefined || v instanceof RegExp,
};
const BooleanValidator = {
    typeDescription: "boolean",
    default: () => undefined,
    validate: (v) => v === undefined || typeof v === "boolean",
};
const LawCriteriaValidator = {
    LawID: RegExpValidator,
    LawNum: RegExpValidator,
    LawTitle: RegExpValidator,
    Enforced: BooleanValidator,
    Path: RegExpValidator,
    XmlName: RegExpValidator,
    ReferencingLawNum: RegExpValidator,
    ReferencedLawNum: RegExpValidator,
    xml: RegExpValidator,
    document: {
        typeDescription: "function",
        default: () => undefined,
        validate: (v) => v === undefined || typeof v === "function",
    },
    el: {
        typeDescription: "function",
        default: () => undefined,
        validate: (v) => v === undefined || typeof v === "function",
    },
};
const getDefaultLawCriteriaArgs = () => getDefaultArgs(LawCriteriaValidator);
class BaseLawCriteria {
    args;
    constructor(args) {
        const defaults = getDefaultLawCriteriaArgs();
        const invalidKeys = [];
        for (const unknownKey in args) {
            if (!(unknownKey in defaults)) {
                invalidKeys.push([unknownKey, "unknown key"]);
                continue;
            }
            const key = unknownKey;
            if (!LawCriteriaValidator[key].validate(args[key])) {
                invalidKeys.push([key, `a value of ${LawCriteriaValidator[key].typeDescription} needed`]);
                continue;
            }
        }
        if (invalidKeys.length > 0) {
            throw new TypeError(`LawCriteria: invalid key(s): ${invalidKeys.map(([key, message]) => `"${key}" (${message})`).join(", ")}`);
        }
        this.args = { ...defaults, ...args };
    }
    async match(item) {
        if (this.args.LawID !== undefined && !this.args.LawID.exec(item.LawID))
            return false;
        if (this.args.LawNum !== undefined && !this.args.LawNum.exec(item.LawNum))
            return false;
        if (this.args.LawTitle !== undefined && !this.args.LawTitle.exec(item.LawTitle))
            return false;
        if (this.args.Enforced !== undefined && this.args.Enforced !== item.Enforced)
            return false;
        if (this.args.Path !== undefined && !this.args.Path.exec(item.Path))
            return false;
        if (this.args.XmlName !== undefined && !this.args.XmlName.exec(item.XmlName))
            return false;
        if (this.args.ReferencingLawNum !== undefined) {
            let matched = false;
            for (const num of item.ReferencingLawNums) {
                if (this.args.ReferencingLawNum.exec(num)) {
                    matched = true;
                    break;
                }
            }
            if (!matched)
                return false;
        }
        if (this.args.ReferencedLawNum !== undefined) {
            let matched = false;
            for (const num of item.ReferencedLawNums) {
                if (this.args.ReferencedLawNum.exec(num)) {
                    matched = true;
                    break;
                }
            }
            if (!matched)
                return false;
        }
        if (this.args.xml !== undefined) {
            const xml = await item.getXML();
            if (xml === null || !this.args.xml.exec(xml))
                return false;
        }
        if (this.args.document !== undefined) {
            const document = await item.getDocument();
            if (document === null || !this.args.document(document))
                return false;
        }
        if (this.args.el !== undefined) {
            const el = await item.getEl();
            if (el === null || !this.args.el(el))
                return false;
        }
        return true;
    }
}
exports.BaseLawCriteria = BaseLawCriteria;
/**
 * {@link LawQuery} で列挙される、法令を表すオブジェクト。
 */
class LawQueryItem extends lawinfo_1.LawInfo {
    loader = null;
    static fromLawInfo(lawInfo, loader) {
        const item = new LawQueryItem(lawInfo.LawID, lawInfo.LawNum, lawInfo.LawTitle, lawInfo.Enforced, lawInfo.Path, lawInfo.XmlName);
        item.ReferencingLawNums = lawInfo.ReferencingLawNums;
        item.ReferencedLawNums = lawInfo.ReferencedLawNums;
        item.loader = loader;
        return item;
    }
    static initialCache = () => ({
        imageData: null,
        xml: null,
        document: null,
        el: null,
    });
    _cache = LawQueryItem.initialCache();
    /**
     * e-Gov 法令APIから法令XMLを取得します。
     * @returns 法令XML
     */
    async getXML() {
        if (this._cache.xml === null) {
            if (this.loader === null)
                throw Error("Loader not specified");
            if (this.loader instanceof FetchElawsLoader_1.FetchElawsLoader) {
                const elawsLawData = await (0, elawsApi_1.fetchLawData)(this.LawID);
                this._cache.xml = elawsLawData.xml;
                this._cache.imageData = elawsLawData.imageData;
            }
            else {
                this._cache.xml = (await this.loader.loadLawXMLStructByInfo(this)).xml;
            }
        }
        return this._cache.xml;
    }
    /**
     * e-Gov 法令APIから法令XMLを取得し、`XMLDocument` として返します。
     * @returns 法令XMLの `XMLDocument`
     */
    async getDocument() {
        if (this._cache.document === null) {
            const xml = await this.getXML();
            if (xml === null)
                return null;
            this._cache.document = domParser.parseFromString(xml, "text/xml");
        }
        return this._cache.document;
    }
    async getEl() {
        if (this._cache.el === null) {
            const doc = await this.getDocument();
            if (doc === null)
                return null;
            this._cache.el = (0, xmlToEL_1.elementToEL)(doc.documentElement);
        }
        return this._cache.el;
    }
    [symbolFinalyzeQueryItem]() {
        if (this[symbolDoNotFinalize])
            return;
        this._cache = LawQueryItem.initialCache();
    }
    [symbolDoNotFinalize] = false;
    /**
     * 「法令名（法令番号）」の形式の文字列を返します。未施行の場合は文頭に「【未施行】」を付します。
     * @returns 文字列
     */
    toString() {
        return `${this.LawID} ${this.Enforced ? "" : "【未施行】"}${this.LawNum}「${this.LawTitle}」`;
    }
}
exports.LawQueryItem = LawQueryItem;
async function* getLawQueryPopulationWithProgress(lawInfosOrPromise, loader, options) {
    const startTime = new Date();
    let lastMessageTime = startTime;
    let yieldCount = 0;
    const lawInfos = typeof lawInfosOrPromise === "function" ? await lawInfosOrPromise() : await lawInfosOrPromise;
    try {
        for (const lawInfo of lawInfos) {
            const item = LawQueryItem.fromLawInfo(lawInfo, loader);
            yieldCount++;
            const now = new Date();
            if (now.getTime() - lastMessageTime.getTime() > 1000) {
                if (options.showProgress)
                    console.info(`   << source:\t⌛ running...\t(${yieldCount.toString().padStart(4, " ")}/${lawInfos.length.toString().padStart(4, " ")}=${Math.floor(yieldCount / lawInfos.length * 100)}%\tin ${now.getTime() - startTime.getTime()} msec)`);
                lastMessageTime = now;
            }
            try {
                yield item;
            }
            finally {
                if (typeof item === "object" && symbolFinalyzeQueryItem in item) {
                    item[symbolFinalyzeQueryItem]();
                }
            }
        }
    }
    finally {
        const now = new Date();
        const msec = now.getTime() - startTime.getTime();
        if (options.showProgress)
            console.info(`   << source:\t${yieldCount === lawInfos.length ? "✓ completed." : "🚧 stopped. "}\t(${yieldCount.toString().padStart(4, " ")}/${lawInfos.length.toString().padStart(4, " ")}=${Math.floor(yieldCount / lawInfos.length * 100)}%\tin ${msec} msec)`);
    }
}
/**
 * Lawtext query の法令検索を行う {@link Query} の派生クラス。ここに列挙されているもの以外のクラスメンバーについては {@link Query} を参照してください。
 */
class LawQuery extends Query {
    constructor(population, criteria, options) {
        let this_criteria;
        if (criteria === null) {
            this_criteria = criteria;
        }
        else if ("match" in criteria) {
            this_criteria = criteria;
        }
        else if (typeof criteria === "function") {
            this_criteria = criteria;
        }
        else {
            this_criteria = new BaseLawCriteria(criteria);
        }
        super(population, this_criteria, options);
    }
    new(population, criteria, overrideOptions) {
        return new LawQuery(population, criteria, { ...this.options, ...overrideOptions });
    }
    static fromFetchInfo(loader, criteria, options) {
        const fullOptions = { ...defaultQueryOptions(), ...options };
        return new LawQuery(getLawQueryPopulationWithProgress((async () => {
            const { lawInfos: lawList } = await loader.cacheLawListStruct();
            return lawList;
        }), loader, fullOptions), criteria, fullOptions);
    }
    filter(criteria) {
        return this.new(this[Symbol.asyncIterator](), criteria);
    }
    assign(func, criteria = null, options) {
        return super.assign(func, criteria, options);
    }
    /**
     * 法令XMLのDOMを取得して追加したオブジェクトを列挙します。
     * @param ensure - 法令XMLが取得できたもののみを列挙するかどうか（デフォルト: `true`）
     * @param options - 上書きするオプション項目
     * @returns - 法令XMLのDOMを `document` プロパティとして追加したオブジェクトを列挙する新しい `Query`
     */
    assignDocument(ensure = true, options) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.new((async function* () {
            for await (const item of self) {
                let document = null;
                try {
                    document = await item.getDocument();
                }
                catch (e) {
                    console.error(e);
                }
                if (!ensure || document !== null) {
                    yield Object.setPrototypeOf({ ...item, document }, Object.getPrototypeOf(item));
                }
            }
        })(), null, options);
    }
}
exports.LawQuery = LawQuery;
//# sourceMappingURL=query.js.map